import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { AuthLayout } from "./Login";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function VerifyOtp() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const email = location.state?.email || "";

  useEffect(() => {
    if (!email) navigate("/signup");
  }, [email, navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      login(data.token, data.user);
      toast.success("Email verified. Welcome!");
      navigate("/studio", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success("New code sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout>
      <div className="text-xs uppercase tracking-[0.24em] text-cyan-400 mb-3">Almost there</div>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">Verify your email</h1>
      <p className="text-sm text-zinc-400 mt-2">
        We sent a 6-digit code to <span className="text-white font-medium">{email}</span>. It expires in 10 minutes.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div className="flex justify-center">
          <InputOTP data-testid="otp-input" maxLength={6} value={code} onChange={(v) => setCode(v)}>
            <InputOTPGroup>
              <InputOTPSlot data-testid="otp-slot-0" index={0} className="h-14 w-12 text-xl bg-white/5 border-white/10 text-white" />
              <InputOTPSlot data-testid="otp-slot-1" index={1} className="h-14 w-12 text-xl bg-white/5 border-white/10 text-white" />
              <InputOTPSlot data-testid="otp-slot-2" index={2} className="h-14 w-12 text-xl bg-white/5 border-white/10 text-white" />
              <InputOTPSlot data-testid="otp-slot-3" index={3} className="h-14 w-12 text-xl bg-white/5 border-white/10 text-white" />
              <InputOTPSlot data-testid="otp-slot-4" index={4} className="h-14 w-12 text-xl bg-white/5 border-white/10 text-white" />
              <InputOTPSlot data-testid="otp-slot-5" index={5} className="h-14 w-12 text-xl bg-white/5 border-white/10 text-white" />
            </InputOTPGroup>
          </InputOTP>
        </div>
        <Button data-testid="otp-submit" type="submit" disabled={loading || code.length !== 6}
          className="btn-primary w-full h-12 rounded-full font-semibold text-base">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
        </Button>
      </form>

      <div className="mt-6 text-sm text-center text-zinc-400">
        Didn't receive the code?{" "}
        <button data-testid="otp-resend" type="button" disabled={resending} onClick={resend}
          className="text-cyan-400 hover:text-cyan-300 font-medium">
          {resending ? "Sending…" : "Resend"}
        </button>
      </div>
      <div className="mt-2 text-xs text-center text-zinc-500">
        <Link to="/login" data-testid="otp-back-login">Back to sign in</Link>
      </div>
    </AuthLayout>
  );
}
